import React, { useCallback, useContext, useEffect, useState } from "react";
import { Clock3, Plus, Trash2 } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, Field, MetricCard, SectionHeader, SurfaceCard, TextareaField } from "../components/designSystem";

function formatEventRange(event) {
  const start = event.start ? new Date(event.start).toLocaleString() : "No start time";
  const end = event.end ? new Date(event.end).toLocaleString() : "Open-ended";
  return `${start} - ${end}`;
}

export default function Calendar() {
  const { token } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", start: "", end: "", allDay: false });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to load events.");
      }
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load events.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchEvents();
    }
  }, [fetchEvents, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/calendar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to save event.");
      }
      setForm({ title: "", description: "", start: "", end: "", allDay: false });
      setEvents((current) => [...current, data].sort((left, right) => new Date(left.start) - new Date(right.start)));
    } catch (err) {
      setError(err.message || "Failed to save event.");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/calendar/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to delete event.");
      }
      setEvents((current) => current.filter((event) => event._id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete event.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_24%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_24%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Trading Calendar"
          title="Schedule reviews, events, and deadlines"
          description="Treat your calendar as part of the trading process: reviews, news sessions, discipline checkpoints, and team calls belong in the operating rhythm."
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Scheduled items" value={String(events.length)} helper="Current events in your calendar." />
          <MetricCard label="All-day events" value={String(events.filter((event) => event.allDay).length)} helper="Sessions or reminders that span the day." />
          <MetricCard label="Timed events" value={String(events.filter((event) => !event.allDay).length)} helper="Specific review and execution windows." />
        </div>
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader eyebrow="New event" title="Add a calendar item" description="Keep upcoming reviews, meetings, and trading deadlines visible in one place." />
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <Field label="Title" name="title" value={form.title} onChange={handleChange} placeholder="Weekly review, CPI prep, team debrief" required />
            <TextareaField label="Description" name="description" value={form.description} onChange={handleChange} rows={4} placeholder="Add the context that makes this event useful later." />
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Start" name="start" type="datetime-local" value={form.start} onChange={handleChange} required />
              <Field label="End" name="end" type="datetime-local" value={form.end} onChange={handleChange} />
            </div>
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-200">
              <input name="allDay" type="checkbox" checked={form.allDay} onChange={handleChange} />
              Mark as an all-day event
            </label>
            <Button type="submit" variant="brand" disabled={saving}>
              <Plus className="h-4 w-4" />
              {saving ? "Saving..." : "Add Event"}
            </Button>
          </form>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader eyebrow="Schedule" title="Upcoming timeline" description="A clean schedule makes review routines easier to maintain over time." />
          <div className="mt-6 space-y-4">
            {loading ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading events...</p>
            ) : events.length ? (
              [...events]
                .sort((left, right) => new Date(left.start) - new Date(right.start))
                .map((event) => (
                  <div key={event._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{event.title}</p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{formatEventRange(event)}</p>
                      </div>
                      <div className="flex gap-2">
                        {event.allDay ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">All day</span> : null}
                        <Button type="button" variant="danger" className="px-3 py-2" onClick={() => handleDelete(event._id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {event.description ? (
                      <div className="mt-4 flex items-start gap-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        <Clock3 className="mt-1 h-4 w-4 text-slate-400" />
                        <p>{event.description}</p>
                      </div>
                    ) : null}
                  </div>
                ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                No calendar events yet. Start by adding review sessions, news events, or discipline checkpoints.
              </div>
            )}
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}
